import React, { useState, useMemo, useCallback, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, useParams, useSearchParams, useLocation, Navigate, Outlet } from 'react-router-dom';
import { useMenu } from './hooks/useMenu';
import { useAuth } from './context/AuthContext';
import { useCart } from './context/CartContext';
import { useRestaurant, RestaurantProvider } from './context/RestaurantContext';
import { MenuItem, Category, Extra, DishReview } from './types';
import { supabase } from './lib/supabase';
import { setDineInTable, getDineInTable, setDineInKind } from './lib/dineIn';
import { socialHandle, instagramUrl, facebookUrl, splitAbout } from './lib/social';
import { PinIcon, PhoneIcon, ClockIcon, GlobeIcon, ExternalIcon, InstagramIcon, FacebookIcon, GoogleIcon } from './components/BrandIcons';
import { hasClickedGoogleReview, markGoogleReviewClicked, hasSeenGooglePopupThisSession, markGooglePopupSeen, normalizeExternalUrl } from './lib/reviewFlags';
import LoyaltyPopupModal, { LoyaltyReward } from './components/LoyaltyPopupModal';
import DailyCardModal from './components/DailyCardModal';
import AssaggiooLogo from './components/AssaggiooLogo';
import { useT, LangToggle } from './i18n';
import './App.css';
// Every page below used to be a top-level import, meaning a customer who just
// scans a QR code for a menu was downloading the entire admin panel (3800+
// lines) and superadmin dashboard (2000+ lines) before seeing anything.
// React.lazy splits each page into its own chunk, fetched only when that
// route is actually visited.
const AuthPage = lazy(() => import('./pages/AuthPage'));
const LuckyBitePage = lazy(() => import('./pages/LuckyBitePage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const CourierLoginPage = lazy(() => import('./pages/CourierLoginPage'));
const CourierDashboardPage = lazy(() => import('./pages/CourierDashboardPage'));
const WaiterLoginPage = lazy(() => import('./pages/WaiterLoginPage'));
const WaiterDashboardPage = lazy(() => import('./pages/WaiterDashboardPage'));
const KitchenLoginPage = lazy(() => import('./pages/KitchenLoginPage'));
const KitchenDashboardPage = lazy(() => import('./pages/KitchenDashboardPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const RewardsPage = lazy(() => import('./pages/RewardsPage'));
const ReservationsPage = lazy(() => import('./pages/ReservationsPage'));
const SuperAdminPage = lazy(() => import('./pages/SuperAdminPage'));
const InfluencerLoginPage = lazy(() => import('./pages/InfluencerLoginPage'));
const InfluencerPortalPage = lazy(() => import('./pages/InfluencerPortalPage'));
const SuperAdminRestaurantFormPage = lazy(() => import('./pages/SuperAdminRestaurantFormPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const DiscoverPage = lazy(() => import('./pages/DiscoverPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const WizardPage = lazy(() => import('./pages/WizardPage'));
const SetupPage = lazy(() => import('./pages/SetupPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const RefundPage = lazy(() => import('./pages/RefundPage'));

const PLACEHOLDER = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&q=80';
function imgSrc(url?: string) { return url && url.trim() ? url : PLACEHOLDER; }
// The item's full photo list (cover first). Falls back to the single image_url for items
// saved before the multi-photo feature, so both old and new rows render the same way.
function galleryOf(item: { images?: string[]; image_url?: string }): string[] {
  const imgs = (item.images || []).filter(u => u && u.trim());
  if (imgs.length) return imgs;
  return item.image_url && item.image_url.trim() ? [item.image_url] : [];
}

const SUPABASE_FN = process.env.REACT_APP_SUPABASE_URL + '/functions/v1';
async function callLoyaltyStatus(restaurantId: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return { enabled: false };
  const res = await fetch(`${SUPABASE_FN}/loyalty-ops`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
    body: JSON.stringify({ action: 'status', restaurant_id: restaurantId }),
  });
  return res.json();
}

const HeartIcon = ({ filled, size = 20 }: { filled: boolean; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? '#ef4444' : 'none'}
    stroke={filled ? '#ef4444' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6"/>
  </svg>
);
const CartIcon = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);
const MenuGridIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);
const FavNavIcon = ({ active }: { active?: boolean }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill={active ? '#ef4444' : 'none'} stroke={active ? '#ef4444' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);
const ReceiptIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16l3-2 2 2 2-2 2 2 2-2 3 2V4a2 2 0 0 0-2-2z"/>
    <line x1="8" y1="9" x2="16" y2="9"/><line x1="8" y1="13" x2="16" y2="13"/>
  </svg>
);
const CalendarNavIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const GiftNavIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/>
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5"/>
  </svg>
);
const InfoOutlineIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);
const UserNavIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const SearchIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

// ── PROTECTED ROUTE (admin del restorantit) ───────────────
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading } = useAuth();
  const { restaurant } = useRestaurant();
  const { slug } = useParams();

  // An admin's own restaurant_id doesn't cover every branch they can manage — chain/
  // multi-location branches are granted separately via restaurant_group_admins (this is
  // exactly what the sidebar branch switcher lets them jump into). Without this check,
  // switching branches always bounced the admin back to a blank login screen.
  //
  // This also covers a real gap discovered while building the à la carte wizard: for any
  // self-serve signup (RegisterPage -> SetupPage, and now the wizard), the auth account is
  // created BEFORE the restaurant exists, so profiles.restaurant_id is still null at that
  // point. The later profiles.upsert({restaurant_id}) that's supposed to backfill it hits
  // protect_profile_privilege_fields on UPDATE, which silently reverts restaurant_id back to
  // null for any non-superadmin session (role stays correct, restaurant_id doesn't stick) —
  // confirmed live against real RLS, not just theorized. restaurant_admins is unaffected by
  // that trigger and is inserted correctly regardless, so it's the one reliable source of
  // truth here — falling back to it is what actually lets a brand-new admin into their own
  // panel instead of being bounced to the login screen right after finishing signup.
  const needsGroupCheck = !!(profile && profile.role === 'admin' && restaurant?.id && profile.restaurant_id !== restaurant.id);
  const [groupGrant, setGroupGrant] = useState<boolean | null>(null);

  useEffect(() => {
    if (!needsGroupCheck || !profile?.id || !restaurant?.id) { setGroupGrant(null); return; }
    let cancelled = false;
    setGroupGrant(null);
    Promise.all([
      supabase.from('restaurant_admins').select('restaurant_id')
        .eq('profile_id', profile.id).eq('restaurant_id', restaurant.id).maybeSingle(),
      supabase.from('restaurant_group_admins').select('restaurant_id')
        .eq('profile_id', profile.id).eq('restaurant_id', restaurant.id).maybeSingle(),
    ]).then(([direct, group]) => { if (!cancelled) setGroupGrant(!!direct.data || !!group.data); });
    return () => { cancelled = true; };
  }, [needsGroupCheck, profile?.id, restaurant?.id]);

  // A profile whose primary role isn't 'admin' (e.g. an existing client account that
  // separately created a business — see restaurant_admins) can still administer this
  // specific restaurant. Checked only as a fallback so the common case (profile.role
  // === 'admin') never pays for the extra round trip.
  const needsOwnerCheck = !!(profile && profile.role !== 'admin' && profile.role !== 'superadmin' && restaurant?.id);
  const [ownerGrant, setOwnerGrant] = useState<boolean | null>(null);

  useEffect(() => {
    if (!needsOwnerCheck || !restaurant?.id) { setOwnerGrant(null); return; }
    let cancelled = false;
    setOwnerGrant(null);
    supabase.rpc('is_admin_of', { target_id: restaurant.id })
      .then(({ data }) => { if (!cancelled) setOwnerGrant(!!data); });
    return () => { cancelled = true; };
  }, [needsOwnerCheck, restaurant?.id]);

  if (loading) return <div className="app-loader"><div className="loader-ring"/></div>;
  if (!user) return <Navigate to={`/r/${slug}/admin/login`} replace />;
  if (profile && profile.role !== 'admin' && profile.role !== 'superadmin') {
    if (needsOwnerCheck) {
      if (ownerGrant === null) return <div className="app-loader"><div className="loader-ring"/></div>;
      if (!ownerGrant) return <Navigate to={`/r/${slug}/admin/login`} replace />;
    } else {
      return <Navigate to={`/r/${slug}/admin/login`} replace />;
    }
  }
  if (needsGroupCheck) {
    if (groupGrant === null) return <div className="app-loader"><div className="loader-ring"/></div>;
    if (!groupGrant) return <Navigate to={`/r/${slug}/admin/login`} replace />;
  }
  return <>{children}</>;
}

// ── PROTECTED ROUTE (korrieri i restorantit) ──────────────
function CourierRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading } = useAuth();
  const { restaurant } = useRestaurant();
  const { slug } = useParams();
  if (loading) return <div className="app-loader"><div className="loader-ring"/></div>;
  if (!user) return <Navigate to={`/r/${slug}/courier/login`} replace />;
  if (profile && (profile.role !== 'courier' || profile.restaurant_id !== restaurant?.id)) {
    return <Navigate to={`/r/${slug}/courier/login`} replace />;
  }
  return <>{children}</>;
}

// ── PROTECTED ROUTE (kamerieri i restorantit) ─────────────
function WaiterRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading } = useAuth();
  const { restaurant } = useRestaurant();
  const { slug } = useParams();
  if (loading) return <div className="app-loader"><div className="loader-ring"/></div>;
  if (!user) return <Navigate to={`/r/${slug}/waiter/login`} replace />;
  if (profile && (profile.role !== 'waiter' || profile.restaurant_id !== restaurant?.id)) {
    return <Navigate to={`/r/${slug}/waiter/login`} replace />;
  }
  return <>{children}</>;
}

// ── PROTECTED ROUTE (kuzhina e restorantit) ───────────────
function KitchenRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading } = useAuth();
  const { restaurant } = useRestaurant();
  const { slug } = useParams();
  if (loading) return <div className="app-loader"><div className="loader-ring"/></div>;
  if (!user) return <Navigate to={`/r/${slug}/kitchen/login`} replace />;
  if (profile && (profile.role !== 'kitchen' || profile.restaurant_id !== restaurant?.id)) {
    return <Navigate to={`/r/${slug}/kitchen/login`} replace />;
  }
  return <>{children}</>;
}

// ── NOT FOUND — top-level catch-all for mistyped/unknown URLs ─────
// A stray fork/knife pair wandering off the plate — the food-menu pun in the
// copy ("isn't on the menu") gets a matching visual instead of a stock icon.
function NotFoundIllustration() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" style={{ overflow: 'visible' }}>
      <circle cx="60" cy="62" r="40" fill="var(--green-pale)" />
      <circle cx="60" cy="62" r="40" stroke="var(--green-mid)" strokeWidth="1.5" strokeDasharray="1 7" strokeLinecap="round" />
      <g stroke="var(--green)" strokeWidth="4" strokeLinecap="round" transform="rotate(-16 47 62)">
        <line x1="47" y1="40" x2="47" y2="86" />
        <line x1="41" y1="40" x2="41" y2="52" />
        <line x1="47" y1="40" x2="47" y2="52" />
        <line x1="53" y1="40" x2="53" y2="52" />
        <path d="M41 40 Q41 52 47 52 Q53 52 53 40" />
      </g>
      <g stroke="var(--green)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" transform="rotate(14 74 62)">
        <line x1="74" y1="40" x2="74" y2="86" />
        <path d="M74 40 C82 44 82 54 74 56" />
      </g>
      <circle cx="90" cy="34" r="5" fill="#F59E0B" />
      <circle cx="24" cy="80" r="3.5" fill="#F59E0B" opacity="0.6" />
    </svg>
  );
}

function NotFoundPage() {
  const t = useT();
  const navigate = useNavigate();
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      background: 'radial-gradient(circle at 18% 15%, var(--green-pale) 0%, transparent 42%), radial-gradient(circle at 88% 82%, #FEF3C7 0%, transparent 38%), var(--bg)',
    }}>
      <div style={{
        textAlign: 'center', maxWidth: 420, width: '100%', background: 'var(--white)',
        borderRadius: 'var(--r-lg)', padding: '44px 36px 40px', boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
          <NotFoundIllustration />
        </div>
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 15, letterSpacing: '.16em', color: 'var(--text-3)', marginBottom: 6 }}>
          404
        </div>
        <h1 style={{ fontFamily: "'DM Serif Display',serif", fontWeight: 400, fontSize: 24, color: 'var(--text)', margin: '0 0 10px' }}>
          {t.notfound_title}
        </h1>
        <p style={{ fontSize: 14.5, color: 'var(--text-2)', lineHeight: 1.7, margin: '0 0 30px' }}>
          {t.notfound_sub}
        </p>
        <button className="green-btn" style={{ padding: '13px 30px', fontSize: 14.5 }} onClick={() => navigate('/')}>
          {t.notfound_home_btn}
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28, opacity: 0.5 }}>
          <AssaggiooLogo size={28} />
        </div>
      </div>
    </div>
  );
}

// Any unmatched sub-path under a resolved restaurant (e.g. /r/taverna-gjika/xyz)
// sends the visitor back to that restaurant's own menu instead of a blank page.
function RestaurantCatchAll() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/r/${slug}`} replace />;
}

// ── RESTAURANT LAYOUT — wraps all /r/:slug routes ─────────
// Lerps a #RRGGBB color toward white by `ratio` (0 = unchanged, 1 = white) —
// used to derive lighter/pale/mid tints from a restaurant's custom accent color.
function mixWithWhite(hex: string, ratio: number): string {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * ratio).toString(16).padStart(2, '0');
  return `#${mix(r)}${mix(g)}${mix(b)}`;
}

function RestaurantLayout() {
  const { restaurant, loading, error } = useRestaurant();
  const { pruneToRestaurant } = useCart();
  const location = useLocation();

  useEffect(() => {
    if (restaurant?.id) pruneToRestaurant(restaurant.id);
  }, [restaurant?.id, pruneToRestaurant]);

  // Real visitors (the SPA) otherwise keep index.html's generic title/description;
  // bots already get a per-restaurant version from bot-meta.php. Mirror the essentials
  // for the browser tab and for JS-rendering crawlers we don't UA-match.
  useEffect(() => {
    if (!restaurant?.name) return;
    const prevTitle = document.title;
    document.title = `${restaurant.name} — Assaggioo`;

    const desc = (restaurant.description || restaurant.tagline || '').trim()
      || `See the full ${restaurant.name} menu and prices online.`;
    let metaEl = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const prevDesc = metaEl?.content ?? null;
    if (!metaEl) { metaEl = document.createElement('meta'); metaEl.name = 'description'; document.head.appendChild(metaEl); }
    metaEl.content = desc;

    let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const hadCanonical = !!linkEl;
    const prevCanonical = linkEl?.href ?? null;
    if (!linkEl) { linkEl = document.createElement('link'); linkEl.rel = 'canonical'; document.head.appendChild(linkEl); }
    linkEl.href = `https://assaggioo.com/r/${restaurant.slug}`;

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null && metaEl) metaEl.content = prevDesc;
      if (linkEl) { if (hadCanonical && prevCanonical !== null) linkEl.href = prevCanonical; else linkEl.remove(); }
    };
  }, [restaurant?.name, restaurant?.description, restaurant?.tagline, restaurant?.slug]);

  if (loading) return <div className="app-loader"><div className="loader-logo">M</div><div className="loader-ring"/></div>;
  if (error || !restaurant) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:12, color:'#ef4444', padding:24, textAlign:'center' }}>
      <div style={{ fontSize:40 }}>🍽️</div>
      <b>Restoranti nuk u gjet.</b>
      <small style={{ color:'var(--text-3)' }}>Kontrollo URL-në ose kontakto administratorin.</small>
    </div>
  );

  // The Assaggioo staff panels (admin, courier, waiter, kitchen) always keep the
  // platform's own brand color — only the customer-facing storefront (menu, cart,
  // orders, customer login) reflects a restaurant's custom accent_color, matching
  // how a Shopify storefront can be themed independently of Shopify's own admin.
  const isPlatformChrome = /\/(admin|courier|waiter|kitchen)(\/|$)/.test(location.pathname);
  const accent = !isPlatformChrome && restaurant.accent_color && /^#[0-9A-Fa-f]{6}$/.test(restaurant.accent_color)
    ? restaurant.accent_color : null;

  const accentVars = accent ? {
    '--green': accent,
    '--green-l': mixWithWhite(accent, 0.22),
    '--green-pale': mixWithWhite(accent, 0.92),
    '--green-mid': mixWithWhite(accent, 0.74),
  } as React.CSSProperties : undefined;

  return accentVars ? <div style={accentVars}><Outlet /></div> : <Outlet />;
}

// ── MENU CARD (list view) ─────────────────────────────────
function MenuCardList({ item, currency, selected, onClick, onLikeCountChange }: {
  item: MenuItem; currency: string; selected: boolean; onClick: () => void;
  onLikeCountChange?: (itemId: number, delta: number) => void;
}) {
  const [imgErr, setImgErr] = useState(false);
  const { favorites, toggleFavorite } = useCart();
  const { restaurant } = useRestaurant();
  const t = useT();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const isFav = favorites.includes(item.id);
  const clientsEnabled = restaurant?.clients_enabled !== false;

  return (
    <div className={`menu-card-list ${selected ? 'selected' : ''}`} onClick={onClick}>
      <div className="card-img-wrap">
        <img src={imgErr ? PLACEHOLDER : imgSrc(item.image_url)} alt={item.name_al||item.name} onError={() => setImgErr(true)} loading="lazy"/>
      </div>
      <div className="card-info">
        <div>
          <div className="card-name">{item.name_al||item.name}</div>
          {(item.description_al||item.description) && <div className="card-desc">{item.description_al||item.description}</div>}
        </div>
        <div className="card-meta">
          <div className="card-price">{Number(item.price).toFixed(0)} {currency}</div>
          {clientsEnabled && (item.like_count ?? 0) > 0 && (
            <span style={{ fontSize:11.5, color:'var(--text-3)', display:'flex', alignItems:'center', gap:3 }}>
              ❤️ {item.like_count}
            </span>
          )}
        </div>
      </div>
      {item.badge && <span className="card-badge-pill">{item.badge}</span>}
      {clientsEnabled && (
        <button className={`card-fav-btn ${isFav ? 'liked' : ''}`} onClick={e => {
          e.stopPropagation();
          if (toggleFavorite(item.id)) onLikeCountChange?.(item.id, isFav ? -1 : 1);
          else navigate(`/r/${slug}/login`);
        }}>
          <HeartIcon filled={isFav} size={17} />
        </button>
      )}
    </div>
  );
}

// ── DETAIL VIEW ───────────────────────────────────────────
function DetailView({ item, currency, allItems, onBack, onSelect, shopEnabled, onLikeCountChange }: {
  item: MenuItem; currency: string; allItems: MenuItem[];
  onBack?: () => void; onSelect: (i: MenuItem) => void; shopEnabled?: boolean;
  onLikeCountChange?: (itemId: number, delta: number) => void;
}) {
  const [imgErr, setImgErr] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [toast, setToast] = useState(false);
  const gallery = useMemo(() => galleryOf(item), [item]);
  const { favorites, toggleFavorite, addToCart } = useCart();
  const { user, signInAsGuest } = useAuth();
  const { restaurant } = useRestaurant();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const t = useT();
  const isFav = favorites.includes(item.id);
  const clientsEnabled = restaurant?.clients_enabled !== false;
  const recently = useMemo(() => allItems.filter(i => i.id !== item.id && i.category_id === item.category_id).slice(0,6), [item,allItems]);

  const [dishReviews, setDishReviews]     = useState<DishReview[]>([]);
  const [commentText, setCommentText]     = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => { setImgErr(false); setActiveImg(0); setQty(1); setCommentText(''); }, [item.id]);

  // dish_reviews.user_id points at auth.users, not profiles, so PostgREST can't
  // embed profiles(full_name) directly — fetch names in a second pass instead.
  const loadDishReviews = useCallback(async () => {
    const { data } = await supabase.from('dish_reviews').select('*')
      .eq('menu_item_id', item.id).order('created_at', { ascending: false });
    const rows = data || [];
    const userIds = Array.from(new Set(rows.map(r => r.user_id).filter(Boolean))) as string[];
    let namesById: Record<string, string> = {};
    if (userIds.length > 0) {
      const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', userIds);
      namesById = Object.fromEntries((profs || []).map(p => [p.id, p.full_name]));
    }
    setDishReviews(rows.map(r => ({ ...r, profiles: r.user_id ? { full_name: namesById[r.user_id] } : null })));
  }, [item.id]);

  useEffect(() => { loadDishReviews(); }, [loadDishReviews]);

  const myDishReview = dishReviews.find(r => r.user_id === user?.id);

  const submitDishComment = async () => {
    let activeUserId = user?.id;
    if (!activeUserId) {
      // Comments don't require a real customer account, just a silent anonymous
      // session (same as dine-in/wheel) — but the whole section is hidden below
      // when clients_enabled is off, so this only ever runs for enabled restaurants.
      await signInAsGuest();
      const { data: { session: guestSession } } = await supabase.auth.getSession();
      activeUserId = guestSession?.user?.id;
      if (!activeUserId) { navigate(`/r/${slug}/login`); return; }
    }
    if (!commentText.trim() || !restaurant?.id) return;
    setSubmittingComment(true);
    const { error } = await supabase.from('dish_reviews').insert({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      user_id: activeUserId,
      comment: commentText.trim(),
    });
    setSubmittingComment(false);
    if (!error) {
      setCommentText('');
      loadDishReviews();
    }
  };

  const handleAdd = () => { addToCart(item, qty); setToast(true); setTimeout(() => setToast(false), 2000); };

  return (
    <div style={{ position:'relative', height:'100%', display:'flex', flexDirection:'column' }}>
      <div className="detail-img-wrap">
        <img src={imgErr ? PLACEHOLDER : imgSrc(gallery[activeImg] || item.image_url)} alt={item.name_al||item.name} onError={() => setImgErr(true)} />
        <div className="detail-img-overlay" />
        {onBack && <button className="detail-img-back" onClick={onBack}><BackIcon /></button>}
        {clientsEnabled && (
          <button className={`detail-img-fav ${isFav ? 'liked' : ''}`} onClick={() => {
            if (toggleFavorite(item.id)) onLikeCountChange?.(item.id, isFav ? -1 : 1);
            else navigate(`/r/${slug}/login`);
          }}>
            <HeartIcon filled={isFav} size={19} />
          </button>
        )}
      </div>
      {gallery.length > 1 && (
        <div className="detail-thumbs">
          {gallery.map((url, i) => (
            <button key={i} className={`detail-thumb ${i === activeImg ? 'active' : ''}`}
              onClick={() => { setImgErr(false); setActiveImg(i); }} aria-label={`Photo ${i + 1}`}>
              <img src={imgSrc(url)} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      <div className="detail-body">
        <div className="detail-name">{item.name_al||item.name}</div>
        <div className="detail-meta">
          {item.badge && <div className="detail-meta-chip">✨ <span>{item.badge}</span></div>}
          {clientsEnabled && (item.like_count ?? 0) > 0 && (
            <div className="detail-meta-chip">❤️ <span>{item.like_count}</span></div>
          )}
        </div>
        <div className="detail-price-row">
          <div className="detail-price">{Number(item.price).toFixed(0)} <small>{currency}</small></div>
          <div className="qty-control">
            <button className="qty-btn" onClick={() => setQty(q => Math.max(1,q-1))}>−</button>
            <span className="qty-num">{qty}</span>
            <button className="qty-btn" onClick={() => setQty(q => q+1)}>+</button>
          </div>
        </div>
        {(item.description_al||item.description) && <>
          <div className="detail-desc-title">{t.menu_detail_about}</div>
          <p className="detail-desc">{item.description_al||item.description}</p>
        </>}

        {recently.length > 0 && <>
          <div className="recently-title">{t.menu_similar}</div>
          <div className="recently-row">
            {recently.map(r => (
              <div key={r.id} className="recently-card" onClick={() => onSelect(r)}>
                <div className="recently-img"><img src={imgSrc(r.image_url)} alt={r.name_al||r.name} loading="lazy"/></div>
                <div className="recently-name">{r.name_al||r.name}</div>
                <div className="recently-price">{Number(r.price).toFixed(0)} {currency}</div>
              </div>
            ))}
          </div>
        </>}

        {/* ── DISH COMMENTS ── */}
        {clientsEnabled && (
          <div className="dish-comments">
            <div className="dish-comments-header">
              <h3>💬 {t.dish_comments_title} {dishReviews.length > 0 ? `(${dishReviews.length})` : ''}</h3>
            </div>

            {myDishReview ? (
              <div className="dish-comments-yours">✓ {t.dish_comment_yours}</div>
            ) : (
              <div className="dish-comment-form">
                <textarea rows={2} placeholder={t.dish_comment_ph} value={commentText}
                  onChange={e => setCommentText(e.target.value)} style={{ width:'100%' }} />
                <div style={{ display:'flex', gap:8, marginTop:8 }}>
                  <button className="green-btn sm" onClick={submitDishComment} disabled={submittingComment || !commentText.trim()}>
                    {submittingComment ? '...' : t.dish_comment_submit}
                  </button>
                  <button className="outline-btn" style={{ padding:'6px 12px', fontSize:12 }} onClick={() => setCommentText('')}>{t.cancel}</button>
                </div>
              </div>
            )}

            {dishReviews.length === 0 ? (
              <p style={{ fontSize:12.5, color:'var(--text-3)' }}>{t.dish_comments_empty}</p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {dishReviews.map(r => (
                  <div key={r.id} className="dish-comment-card">
                    <div className="dish-comment-name">{r.profiles?.full_name || t.dish_comment_anon}</div>
                    <div className="dish-comment-text">{r.comment}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{height:90}} />
      </div>
      {shopEnabled !== false && (
        <div className="detail-action-bar">
          {clientsEnabled && (
            <button className={`fav-btn-bar ${isFav ? 'liked' : ''}`} onClick={() => {
              if (toggleFavorite(item.id)) onLikeCountChange?.(item.id, isFav ? -1 : 1);
              else navigate(`/r/${slug}/login`);
            }}>
              <HeartIcon filled={isFav} size={21} />
            </button>
          )}
          <button className="add-cart-btn" onClick={handleAdd}>
            <CartIcon />
            <span>{t.menu_add_btn} {(Number(item.price)*qty).toFixed(0)} {currency}</span>
          </button>
        </div>
      )}
      <div className={`toast ${toast ? 'show' : ''}`}>{t.menu_added_toast}</div>
    </div>
  );
}

// ── MENU PAGE ─────────────────────────────────────────────
function MenuPage() {
  const { restaurant } = useRestaurant();
  const { categories, menuItems, setMenuItems, loading, error, offline, expired } = useMenu(restaurant);
  const handleLikeCountChange = useCallback((itemId: number, delta: number) => {
    setMenuItems(prev => prev.map(i => i.id === itemId ? { ...i, like_count: Math.max(0, (i.like_count ?? 0) + delta) } : i));
  }, [setMenuItems]);
  const { user, profile, signOut, signInAsGuest, isGuest } = useAuth();
  const { cart, cartCount, cartTotal, favorites, updateQty,
          cartExtras, extrasCount, changeCartExtra } = useCart();
  // A table order can go through even when Shop/Delivery is off — dine-in is its own
  // independent package, not a subset of Shop (see CartPage/OrdersPage for the matching check).
  const hasDineInTable     = !!(restaurant?.dine_in_enabled && restaurant?.id && getDineInTable(restaurant.id));
  const shopEnabled        = restaurant?.shop_enabled !== false || hasDineInTable;
  const clientsEnabled     = restaurant?.clients_enabled  !== false;
  // Loyalty progress only ever comes from courier-delivered orders (loyalty-ops explicitly
  // excludes dine-in/waiter orders) — a restaurant with no delivery channel has no way for
  // a customer to ever earn it, so it's not worth surfacing an entry point for.
  const loyaltyFunctional  = !!(restaurant?.loyalty_enabled && restaurant?.shop_enabled);
  const t = useT();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const table = searchParams.get('table');
    if (table && restaurant?.dine_in_enabled && restaurant?.id) {
      setDineInTable(restaurant.id, table);
      const kind = searchParams.get('kind');
      setDineInKind(restaurant.id, kind === 'room' ? 'room' : kind === 'table' ? 'table' : null);
    }
  }, [searchParams, restaurant?.dine_in_enabled, restaurant?.id]);

  // Tablet/dine-in ordering shouldn't require a personal login — as soon as we know
  // we're at a table (fresh ?table= param, or one already saved from this visit),
  // silently start a guest session so ordering/comments/wheel all work immediately.
  useEffect(() => {
    if (user || !restaurant?.dine_in_enabled || !restaurant?.id) return;
    const table = searchParams.get('table') || getDineInTable(restaurant.id);
    if (table) signInAsGuest();
  }, [user, restaurant?.dine_in_enabled, restaurant?.id, searchParams, signInAsGuest]);

  // Lucky Bite moved to its own dedicated page (/r/:slug/lucky), reached only via a link/QR
  // the restaurant displays physically in-store — see LuckyBitePage. That link possession
  // *is* the proof of presence now; the menu itself no longer runs any wheel logic, and the
  // geolocation-based proximity check (which depended on phone GPS accuracy and a browser
  // permission prompt that mobile browsers often refuse to show automatically) is gone.
  //
  // Loyalty is unaffected — still a perk for real Assaggioo accounts only: an anonymous
  // guest session gets a brand-new identity every visit, so "have you already won" and any
  // returning-customer reporting would be meaningless for them.
  const [loyaltyReward, setLoyaltyReward] = useState<LoyaltyReward | null>(null);
  // A claim the customer has already redeemed and hasn't used yet — distinct from
  // loyaltyReward above (that's the "you just became eligible, claim it" popup
  // trigger). This drives the inline cart preview's own discount/gift display,
  // which used to show the raw undiscounted total since it never checked for one.
  const [activeLoyaltyClaim, setActiveLoyaltyClaim] = useState<any>(null);
  const loyaltyCheckedRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!user || isGuest || !loyaltyFunctional || !restaurant?.id) { setActiveLoyaltyClaim(null); return; }
    const key = `${restaurant.id}:${user.id}`;
    if (loyaltyCheckedRef.current === key) return;
    loyaltyCheckedRef.current = key;
    callLoyaltyStatus(restaurant.id).then(result => {
      if (result?.ready && !result?.alreadyClaimed && result?.reward) {
        setLoyaltyReward(result.reward);
      }
      if (result?.alreadyClaimed && result?.claim && !result.claim.used_at) {
        setActiveLoyaltyClaim(result.claim);
      }
    }).catch(() => {});
  }, [user, isGuest, loyaltyFunctional, restaurant?.id]);

  // Daily scratch card: an optional per-restaurant perk (restaurant.daily_card_enabled). Shown
  // at most once per restaurant per day on a device — the day is remembered in localStorage
  // *when it is shown*, so a refresh or coming back later never re-opens it. It sits over the
  // menu with an always-visible Skip and never delays the menu itself. `?dailycard=preview`
  // forces it open for demos/testing (still requires the feature to be on for the restaurant).
  const [showDailyCard, setShowDailyCard] = useState(false);
  const dailyCardDay = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  useEffect(() => {
    if (!restaurant?.id || !restaurant.daily_card_enabled) return;
    const preview = new URLSearchParams(window.location.search).get('dailycard') === 'preview';
    const key = `daily_card_${restaurant.id}`;
    try {
      if (!preview && localStorage.getItem(key) === dailyCardDay) return;
      if (!preview) localStorage.setItem(key, dailyCardDay);
    } catch { /* storage blocked: show it, worst case it repeats */ }
    setShowDailyCard(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.id, restaurant?.daily_card_enabled]);

  // Same {menu_item_id, qty} composition + discount math CartPage.tsx uses at
  // checkout — kept in sync here so the pre-checkout preview shows the same total.
  const activeRewardItems: { menu_item_id: number; qty: number }[] = activeLoyaltyClaim?.reward_items || [];
  const previewLoyaltyDiscount = (() => {
    if (!activeLoyaltyClaim) return 0;
    if (activeLoyaltyClaim.reward_type === 'discount_percent') {
      return cartTotal * (Number(activeLoyaltyClaim.discount_percent) || 0) / 100;
    }
    if (activeLoyaltyClaim.reward_type === 'menu_item' && activeRewardItems.length > 0) {
      return activeRewardItems.reduce((sum, ri) => {
        const line = cart.find(c => c.item.id === ri.menu_item_id);
        if (!line) return sum;
        return sum + line.item.price * Math.min(line.qty, ri.qty);
      }, 0);
    }
    return 0;
  })();

  // A quantity of one dish, or a combo of several — joins into "2× Tiramisu" or
  // "Tiramisu + Panna Cotta" when the admin hasn't set a custom label of their own.
  // Derived (not frozen at fetch time) so it fills in once menuItems finishes loading,
  // even if that happens after the loyalty-ops call resolves.
  const loyaltyMenuItemName = React.useMemo(() => {
    if (!loyaltyReward || loyaltyReward.type !== 'menu_item' || !loyaltyReward.items || loyaltyReward.items.length === 0) return null;
    return loyaltyReward.items.map(ri => {
      const mi = menuItems.find(m => m.id === ri.menu_item_id);
      const name = mi?.name_al || mi?.name || '';
      return name ? (ri.qty > 1 ? `${ri.qty}× ${name}` : name) : '';
    }).filter(Boolean).join(' + ') || null;
  }, [loyaltyReward, menuItems]);

  const [activeCat, setActiveCat] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<MenuItem | null>(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [activeNav, setActiveNav] = useState('menu');
  const [showSearch, setShowSearch]   = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showAbout, setShowAbout]     = useState(false);
  const [extras, setExtras]           = useState<Extra[]>([]);
  const [showGooglePopup, setShowGooglePopup] = useState(false);
  const [googleReviewed, setGoogleReviewed] = useState(false);

  useEffect(() => {
    setGoogleReviewed(hasClickedGoogleReview(restaurant?.id));
  }, [restaurant?.id]);

  useEffect(() => {
    if (!restaurant?.google_review_url || !restaurant?.id) return;
    if (hasClickedGoogleReview(restaurant.id) || hasSeenGooglePopupThisSession(restaurant.id)) return;
    const timer = setTimeout(() => {
      setShowGooglePopup(true);
      markGooglePopupSeen(restaurant.id);
    }, 15000);
    return () => clearTimeout(timer);
  }, [restaurant?.google_review_url, restaurant?.id]);

  const openGoogleReview = () => {
    if (restaurant?.id) markGoogleReviewClicked(restaurant.id);
    if (restaurant?.google_review_url) window.open(normalizeExternalUrl(restaurant.google_review_url), '_blank');
    setGoogleReviewed(true);
    setShowGooglePopup(false);
  };

  useEffect(() => {
    // Plain overflow:hidden doesn't actually stop touch-scrolling the body on most
    // mobile browsers (iOS Safari in particular) — pinning the body in place with
    // position:fixed and restoring the scroll position on close is the reliable
    // way to keep the page behind a modal from scrolling underneath it.
    if (!(showProfile || showAbout)) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [showProfile, showAbout]);

  const currency = restaurant?.currency || 'ALL';

  useEffect(() => {
    if (!restaurant) return;
    supabase.from('extras').select('*')
      .eq('restaurant_id', restaurant.id)
      .eq('is_available', true)
      .order('sort_order')
      .then(({ data }) => setExtras(data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.id]); // restaurant?.id as stable primitive is intentional

  const extrasTotal = extras
    .filter(e => (cartExtras[e.id] || 0) > 0)
    .reduce((s, e) => s + e.price * (cartExtras[e.id] || 0), 0);

  const favoriteItems = useMemo(() => menuItems.filter(i => favorites.includes(i.id)), [menuItems, favorites]);

  const filteredItems = useMemo(() => {
    let items = activeNav === 'fav' ? favoriteItems : menuItems;
    if (activeCat !== null) items = items.filter(i => i.category_id === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(i => (i.name_al||i.name).toLowerCase().includes(q) || (i.description_al||i.description||'').toLowerCase().includes(q));
    }
    return items;
  }, [menuItems, favoriteItems, activeCat, search, activeNav]);

  // Opening the mobile dish-detail overlay pushes a history entry so the phone's
  // back button/gesture closes it instead of leaving the site entirely (it was
  // pure component state before, invisible to browser history).
  useEffect(() => {
    const onPopState = () => { setMobileDetail(false); setTimeout(() => setSelected(null), 350); };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const handleSelect = useCallback((item: MenuItem) => {
    setSelected(item);
    if (window.innerWidth <= 900) { setMobileDetail(true); window.history.pushState({ dishDetail: true }, ''); }
  }, []);
  const handleBack = useCallback(() => {
    if (window.history.state?.dishDetail) window.history.back();
    else { setMobileDetail(false); setTimeout(() => setSelected(null), 350); }
  }, []);

  if (loading) return <div className="app-loader"><div className="loader-logo">{restaurant?.name?.charAt(0)||'M'}</div><div className="loader-ring"/></div>;

  // Trial expired, deliberately deactivated, not yet started, or a genuine fetch/platform
  // failure — a customer scanning a QR code can't tell those apart and shouldn't have to.
  // Every one of them shows this same soft "we're updating" wall instead of a technical
  // error or an "out of service" message that reads like something's actually broken.
  if (expired || error) return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      background:'linear-gradient(135deg, var(--green-pale) 0%, var(--white) 50%, var(--green-pale) 100%)',
      padding:24, textAlign:'center' }}>
      <div style={{ maxWidth:440, width:'100%' }}>
        {restaurant?.icon_url
          ? <img src={restaurant.icon_url} alt={restaurant.name}
              style={{ width:80, height:80, borderRadius:20, objectFit:'cover', marginBottom:24, boxShadow:'0 4px 24px rgba(0,0,0,.12)' }} />
          : <div style={{ width:80, height:80, borderRadius:20, background:'var(--green)', color:'white', display:'flex',
              alignItems:'center', justifyContent:'center', fontSize:36, fontWeight:700, margin:'0 auto 24px', boxShadow:'0 4px 24px rgba(107,39,55,.25)' }}>
              {restaurant?.name?.charAt(0) || '🍽️'}
            </div>
        }
        <div style={{ fontSize:48, marginBottom:16 }}>✨</div>
        <h1 style={{ fontFamily:'DM Serif Display,serif', fontSize:28, color:'var(--text)', margin:'0 0 12px' }}>
          {t.trial_soon_title}
        </h1>
        <p style={{ fontSize:16, color:'var(--text-2)', lineHeight:1.6, margin:'0 0 24px' }}>
          {t.trial_soon_sub}
        </p>
        <div style={{ fontSize:18, fontWeight:700, color:'var(--text)' }}>{restaurant?.name}</div>
      </div>
    </div>
  );

  const offlineBanner = offline ? (
    <div style={{position:'fixed',top:0,left:0,right:0,background:'#F59E0B',color:'white',fontSize:12,fontWeight:600,textAlign:'center',padding:'6px',zIndex:999}}>
      {t.menu_offline}
    </div>
  ) : null;

  const allCats = [{ id: null, name_al: t.menu_all_cats, icon: '✨' }, ...categories];

  return (
    <div className="app-shell">
      {offlineBanner}
      <aside className="sidebar">
        <nav className="sidebar-nav">
          <button className={`sidebar-btn ${activeNav==='menu'?'active':''}`} onClick={() => setActiveNav('menu')} title="Menu">
            <MenuGridIcon />
          </button>
          {clientsEnabled && (
            <button className={`sidebar-btn ${activeNav==='fav'?'active':''}`} onClick={() => setActiveNav('fav')} title="Favorite">
              <FavNavIcon active={activeNav==='fav'} />
            </button>
          )}
          {shopEnabled && (
            <button className="sidebar-btn" onClick={() => navigate(`/r/${slug}/orders`)} title={t.sidebar_orders}>
              <ReceiptIcon />
            </button>
          )}
          {(restaurant?.wheel_enabled || loyaltyFunctional) && (
            <button className="sidebar-btn" onClick={() => navigate(`/r/${slug}/rewards`)} title={t.sidebar_rewards}>
              <GiftNavIcon />
            </button>
          )}
          {restaurant?.reservations_enabled && (
            <button className="sidebar-btn" onClick={() => navigate(`/r/${slug}/reservations`)} title={t.sidebar_reservations}>
              <CalendarNavIcon />
            </button>
          )}
          <button className="sidebar-btn" onClick={() => setShowAbout(true)} title={t.sidebar_about}>
            <InfoOutlineIcon />
          </button>
        </nav>
        <div className="sidebar-bottom">
          {shopEnabled && (
            <button className={`sidebar-btn ${activeNav==='cart'?'active':''}`} onClick={() => setActiveNav('cart')} title={t.sidebar_cart} style={{position:'relative'}}>
              <CartIcon size={21} />
              {cartCount > 0 && <span className="sidebar-badge">{cartCount}</span>}
            </button>
          )}
          {user ? (
            <button className="sidebar-btn" onClick={() => setShowProfile(true)}>
              <div style={{width:28,height:28,minWidth:28,minHeight:28,borderRadius:'50%',background:'var(--green)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,flexShrink:0,aspectRatio:'1'}}>
                {(profile?.full_name||user.email||'U').charAt(0).toUpperCase()}
              </div>
            </button>
          ) : clientsEnabled ? (
            <button className="sidebar-btn" onClick={() => navigate(`/r/${slug}/login`)}><UserNavIcon /></button>
          ) : null}
          <LangToggle className="sidebar-lang" />
        </div>
      </aside>

      <div className="main-content">
        <div className="list-panel">
          <div className="list-header">
            <div className="list-header-top">
              <div className="list-title-group">
                {activeNav !== 'fav' && activeNav !== 'cart' && (
                  <button className="list-logo-btn" onClick={() => setShowAbout(true)} title="Rreth restorantit">
                    {restaurant?.icon_url
                      ? <img src={restaurant.icon_url} alt={restaurant.name} />
                      : <span>{restaurant?.name?.charAt(0)||'M'}</span>
                    }
                  </button>
                )}
                <h1 className="list-title">
                  {activeNav==='fav' ? t.bnav_favorites : activeNav==='cart' ? t.bnav_cart : (restaurant?.name||'Menu')}
                </h1>
              </div>
              <div className="header-actions">
                {activeNav !== 'cart' && (
                  <button className="icon-btn" onClick={() => setShowSearch(!showSearch)}><SearchIcon /></button>
                )}
                {shopEnabled && (
                  <button className={`icon-btn ${activeNav==='cart'?'active':''}`} onClick={() => setActiveNav('cart')} style={{position:'relative'}}>
                    <CartIcon size={17} />
                    {cartCount>0&&<span className="cart-badge-pill">{cartCount}</span>}
                  </button>
                )}
                {user ? (
                  <button className="icon-btn" onClick={() => setShowProfile(true)}>
                    <div style={{width:24,height:24,minWidth:24,minHeight:24,borderRadius:'50%',background:'var(--green)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0,aspectRatio:'1'}}>
                      {(profile?.full_name||user.email||'U').charAt(0).toUpperCase()}
                    </div>
                  </button>
                ) : clientsEnabled ? (
                  <button className="icon-btn login-btn" onClick={() => navigate(`/r/${slug}/login`)}>{t.nav_login}</button>
                ) : null}
              </div>
            </div>
            {activeNav !== 'cart' && showSearch && (
              <div className="search-box">
                <span className="search-icon"><SearchIcon /></span>
                <input autoFocus type="text" placeholder={t.menu_search_ph} value={search}
                  onChange={e => { setSearch(e.target.value); setActiveCat(null); }} />
                {search && <button className="search-clear" onClick={() => setSearch('')}>✕</button>}
              </div>
            )}
            {activeNav !== 'fav' && activeNav !== 'cart' && (
              <div className="cat-scroll">
                <div className="cat-tabs">
                  {allCats.map(cat => (
                    <button key={cat.id??'all'} className={`cat-tab ${activeCat===(cat.id??null)?'active':''}`}
                      onClick={() => { setActiveCat(cat.id??null); setSearch(''); }}>
                      {cat.id===null ? t.menu_all_cats : (cat as Category).name_al||(cat as Category).name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="menu-list">
            {activeNav === 'cart' ? (
              cart.length === 0 && extras.length === 0 ? (
                <div className="empty-search">
                  <div style={{fontSize:48}}>🛒</div>
                  <p>{t.menu_cart_empty}</p>
                </div>
              ) : (<>
                {activeLoyaltyClaim && (
                  <div style={{ margin:'0 0 12px', padding:'10px 14px', borderRadius:12,
                    background:'var(--green-pale)', border:'1.5px solid var(--green)',
                    fontSize:13, fontWeight:600, color:'var(--green)' }}>
                    🎁 {t.loyalty_active_claim_banner} {activeLoyaltyClaim.reward_type === 'discount_percent'
                      ? `${activeLoyaltyClaim.discount_percent}% ${t.loyalty_popup_off}`
                      : (activeLoyaltyClaim.reward_label || t.loyalty_reward_fallback)}
                  </div>
                )}
                {cart.map(c => {
                  const giftRule = activeRewardItems.find(ri => ri.menu_item_id === c.item.id);
                  const atGiftFloor = !!giftRule && c.qty <= giftRule.qty;
                  return (
                  <div key={c.item.id} className="cart-inline-row">
                    <div className="cart-inline-img">
                      <img src={imgSrc(c.item.image_url)} alt={c.item.name_al||c.item.name} loading="lazy"/>
                    </div>
                    <div className="cart-inline-info">
                      <div className="cart-inline-name">
                        {c.item.name_al||c.item.name}
                        {giftRule && (
                          <span style={{ marginLeft:6, fontSize:11, fontWeight:700, color:'var(--green)', background:'var(--white)', padding:'2px 8px', borderRadius:20 }}>
                            🎁 {t.cart_gift_tag}
                          </span>
                        )}
                      </div>
                      <div className="cart-inline-uprice">{Number(c.item.price).toFixed(0)} {currency} {t.per_piece}</div>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,flexShrink:0}}>
                      <div className="qty-control">
                        <button className="qty-btn" onClick={() => updateQty(c.item.id, c.qty - 1)} disabled={atGiftFloor}
                          title={atGiftFloor ? t.cart_gift_locked_hint : undefined}>−</button>
                        <span className="qty-num">{c.qty}</span>
                        <button className="qty-btn" onClick={() => updateQty(c.item.id, c.qty + 1)}>+</button>
                      </div>
                      <div className="cart-inline-subtotal">{(c.item.price * c.qty).toFixed(0)} {currency}</div>
                    </div>
                  </div>
                  );
                })}
                {extras.length > 0 && (
                  <div className="cart-extras-section">
                    <div className="cart-extras-label">{t.menu_extras_label}</div>
                    {extras.map(ex => {
                      const qty = cartExtras[ex.id] || 0;
                      return (
                        <div key={ex.id} className="cart-inline-row">
                          <div className="cart-inline-img">
                            <img src={imgSrc(ex.image_url)} alt={ex.name_al||ex.name} loading="lazy"/>
                          </div>
                          <div className="cart-inline-info">
                            <div className="cart-inline-name">{ex.name_al||ex.name}</div>
                            <div className="cart-inline-uprice" style={{color:'#7C3AED'}}>
                              {Number(ex.price) === 0 ? t.free : `${Number(ex.price).toFixed(0)} ${currency}`}
                            </div>
                          </div>
                          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,flexShrink:0}}>
                            <div className="qty-control">
                              <button className="qty-btn" onClick={() => changeCartExtra(ex.id, -1)} disabled={qty===0}>−</button>
                              <span className="qty-num">{qty}</span>
                              <button className="qty-btn" onClick={() => changeCartExtra(ex.id, 1)}>+</button>
                            </div>
                            {qty > 0 && (
                              <div className="cart-inline-subtotal" style={{color:'#7C3AED'}}>
                                {Number(ex.price) === 0 ? 'Falas' : `${(ex.price * qty).toFixed(0)} ${currency}`}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>)
            ) : filteredItems.length===0 ? (
              <div className="empty-search">
                <div style={{fontSize:48}}>{activeNav==='fav'?'💚':'🍽️'}</div>
                <p>{activeNav==='fav' ? t.menu_fav_empty : t.menu_no_results}</p>
              </div>
            ) : (<>
              {filteredItems.map(item => (
                <MenuCardList key={item.id} item={item} currency={currency} selected={selected?.id===item.id} onClick={() => handleSelect(item)} onLikeCountChange={handleLikeCountChange} />
              ))}

            </>)}
          </div>
          {activeNav === 'cart' && (cart.length > 0 || Object.values(cartExtras).some(v => v > 0)) && (
            <div className="cart-inline-footer">
              {extrasTotal > 0 && (
                <div className="cart-inline-total" style={{fontSize:12,color:'#7C3AED',marginBottom:2}}>
                  <span>{t.cart_extras_label}</span>
                  <span>+{extrasTotal.toFixed(0)} {currency}</span>
                </div>
              )}
              {(() => {
                const df = Number(restaurant?.delivery_fee) || 0;
                return (
                  <div className="cart-inline-total" style={{fontSize:12, marginBottom:2,
                    color: df === 0 ? 'var(--green)' : 'var(--text-2)'}}>
                    <span>{t.menu_delivery}</span>
                    <span>{df === 0 ? t.free : `${df.toFixed(0)} ${currency}`}</span>
                  </div>
                );
              })()}
              {previewLoyaltyDiscount > 0 && (
                <div className="cart-inline-total" style={{fontSize:12, marginBottom:2, color:'var(--green)'}}>
                  <span>🎁 {t.cart_loyalty_discount_label}</span>
                  <span>−{previewLoyaltyDiscount.toFixed(0)} {currency}</span>
                </div>
              )}
              <div className="cart-inline-total">
                <span>{t.menu_total}</span>
                <strong className="cart-inline-total-val">
                  {Math.max(0, cartTotal + extrasTotal + (Number(restaurant?.delivery_fee) || 0) - previewLoyaltyDiscount).toFixed(0)} {currency}
                </strong>
              </div>
              <button className="add-cart-btn" style={{width:'100%',height:50,marginTop:10}}
                onClick={() => navigate(`/r/${slug}/cart`)}>
                <CartIcon size={16} /><span>{t.menu_continue_order}</span>
              </button>
            </div>
          )}
        </div>
        <div className="detail-panel">
          {selected ? <DetailView item={selected} currency={currency} allItems={menuItems} onSelect={handleSelect} shopEnabled={shopEnabled} onLikeCountChange={handleLikeCountChange} />
            : <div className="detail-empty"><div className="detail-empty-icon">🍽️</div><p>{t.menu_detail_empty}</p></div>}
        </div>
      </div>

      <div className={`mobile-detail-modal ${mobileDetail?'open':''}`}>
        {selected && <DetailView item={selected} currency={currency} allItems={menuItems} onBack={handleBack} onSelect={handleSelect} shopEnabled={shopEnabled} onLikeCountChange={handleLikeCountChange} />}
      </div>

      {/* ── RRETH NESH PANEL ── */}
      {showAbout && (
        <div className="about-overlay" onClick={() => setShowAbout(false)}>
          <div className="about-panel" onClick={e => e.stopPropagation()}>
            <button className="about-close" onClick={() => setShowAbout(false)} aria-label={t.about_close}>✕</button>
            {restaurant?.cover_url && (
              <div className="about-cover">
                <img src={restaurant.cover_url} alt={restaurant.name} />
              </div>
            )}
            <div className="about-body">
              <div className="about-logo">
                {restaurant?.icon_url
                  ? <img src={restaurant.icon_url} alt={restaurant.name} />
                  : <span>{restaurant?.name?.charAt(0)}</span>
                }
              </div>
              <h2 className="about-name">{restaurant?.name}</h2>
              {restaurant?.tagline && <p className="about-tagline">{restaurant.tagline}</p>}
              {restaurant?.description && <p className="about-desc">{restaurant.description}</p>}
              {restaurant?.about && (() => {
                const { text, credits } = splitAbout(restaurant.about);
                return (
                  <>
                    {text && <div className="about-text">{text}</div>}
                    {credits && (
                      <details className="about-credits">
                        <summary>{t.about_photo_credits}</summary>
                        <p>{credits}</p>
                      </details>
                    )}
                  </>
                );
              })()}
              <div className="about-info-list">
                {restaurant?.address && (
                  <div className="about-info-row"><span className="ico"><PinIcon /></span><span>{restaurant.address}</span></div>
                )}
                {restaurant?.phone && (
                  <div className="about-info-row"><span className="ico"><PhoneIcon /></span><a href={`tel:${restaurant.phone}`}>{restaurant.phone}</a></div>
                )}
                {restaurant?.opening_hours && (
                  <div className="about-info-row"><span className="ico"><ClockIcon /></span><span>{restaurant.opening_hours}</span></div>
                )}
                {restaurant?.website && (
                  <div className="about-info-row"><span className="ico"><GlobeIcon /></span><a href={restaurant.website} target="_blank" rel="noopener noreferrer">{restaurant.website.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')}</a></div>
                )}
              </div>
              {(restaurant?.lat || restaurant?.google_review_url || restaurant?.instagram || restaurant?.facebook) && (
                <div className="about-links">
                  {(restaurant?.lat && restaurant?.lng) && (
                    <a href={`https://www.google.com/maps?q=${restaurant.lat},${restaurant.lng}`}
                      target="_blank" rel="noopener noreferrer" className="about-maps-btn">
                      <PinIcon size={20} /> {t.about_maps_btn}
                    </a>
                  )}
                  <div className="about-link-list">
                    {restaurant?.google_review_url && (
                      <button type="button" className="about-link" onClick={openGoogleReview} aria-label={`${t.google_review_fab_title} (${t.about_opens_new_tab})`}>
                        <span className="ico"><GoogleIcon /></span>
                        <span className="lb">{t.google_review_fab_title}</span>
                        <span className="ext"><ExternalIcon /></span>
                      </button>
                    )}
                    {restaurant?.instagram && socialHandle(restaurant.instagram) && (
                      <a href={instagramUrl(restaurant.instagram)} target="_blank" rel="noopener noreferrer" className="about-link"
                        aria-label={`Instagram @${socialHandle(restaurant.instagram)} (${t.about_opens_new_tab})`}>
                        <span className="ico"><InstagramIcon /></span>
                        <span className="lb">Instagram<span className="sub">@{socialHandle(restaurant.instagram)}</span></span>
                        <span className="ext"><ExternalIcon /></span>
                      </a>
                    )}
                    {restaurant?.facebook && socialHandle(restaurant.facebook) && (
                      <a href={facebookUrl(restaurant.facebook)} target="_blank" rel="noopener noreferrer" className="about-link"
                        aria-label={`Facebook (${t.about_opens_new_tab})`}>
                        <span className="ico"><FacebookIcon /></span>
                        <span className="lb">Facebook</span>
                        <span className="ext"><ExternalIcon /></span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── PROFILE MODAL ── */}
      {showProfile && user && (
        <div className="profile-overlay" onClick={() => setShowProfile(false)}>
          <div className="profile-panel" onClick={e => e.stopPropagation()}>
            <button className="profile-close" onClick={() => setShowProfile(false)}>✕</button>
            <div className="profile-avatar-lg">
              {isGuest ? '🍽️' : (profile?.full_name || user.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="profile-name">{isGuest ? t.profile_guest_name : (profile?.full_name || t.profile_user_fallback)}</div>
            {isGuest ? (
              <div className="profile-email">{t.profile_guest_hint}</div>
            ) : (
              <div className="profile-email">{user.email}</div>
            )}
            {profile?.phone && <div className="profile-phone">📞 {profile.phone}</div>}
            <div className="profile-actions">
              {shopEnabled && (
                <button className="profile-action-btn" onClick={() => { setShowProfile(false); navigate(`/r/${slug}/orders`); }}>
                  <span>📋</span> {t.profile_my_orders}
                </button>
              )}
              {(restaurant?.wheel_enabled || loyaltyFunctional) && (
                <button className="profile-action-btn" onClick={() => { setShowProfile(false); navigate(`/r/${slug}/rewards`); }}>
                  <span>🎁</span> {t.profile_my_rewards}
                </button>
              )}
              {restaurant?.reservations_enabled && (
                <button className="profile-action-btn" onClick={() => { setShowProfile(false); navigate(`/r/${slug}/reservations`); }}>
                  <span style={{ display:'inline-flex', verticalAlign:'middle' }}><CalendarNavIcon /></span> {t.sidebar_reservations}
                </button>
              )}
              {(profile?.role === 'admin' || profile?.role === 'superadmin') && (
                <button className="profile-action-btn" onClick={() => { setShowProfile(false); navigate(profile?.role === 'superadmin' ? '/super-admin' : `/r/${slug}/admin`); }}>
                  <span>⚙</span> {t.profile_admin_panel}
                </button>
              )}
              <button className="profile-action-btn danger" onClick={() => { signOut(); setShowProfile(false); }}>
                <span>↩</span> {t.profile_logout}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDailyCard && restaurant && (
        <DailyCardModal
          restaurantId={restaurant.id}
          restaurantName={restaurant.name}
          logoUrl={restaurant.logo_url || restaurant.icon_url}
          isoDay={dailyCardDay}
          onClose={() => setShowDailyCard(false)}
        />
      )}

      {loyaltyReward && restaurant && !showDailyCard && (
        <LoyaltyPopupModal
          restaurantId={restaurant.id}
          reward={loyaltyReward}
          menuItemName={loyaltyMenuItemName}
          onClaimed={() => {}}
          onClose={() => setLoyaltyReward(null)}
        />
      )}

      {/* ── GOOGLE REVIEW: popup on open ── */}
      {showGooglePopup && !googleReviewed && restaurant && (
        <div className="profile-overlay" onClick={() => setShowGooglePopup(false)}>
          <div className="profile-panel" onClick={e => e.stopPropagation()} style={{ textAlign:'center', maxWidth:340 }}>
            <button className="profile-close" onClick={() => setShowGooglePopup(false)}>✕</button>
            <div style={{ fontSize:44, marginBottom:10 }}>⭐</div>
            <div style={{ fontWeight:700, fontSize:16, color:'var(--text)', marginBottom:6 }}>
              {t.google_popup_title} {restaurant.name}?
            </div>
            <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:18 }}>{t.google_popup_sub}</p>
            <button className="green-btn" style={{ width:'100%' }} onClick={openGoogleReview}>
              {t.google_popup_cta}
            </button>
            <button className="link-btn" style={{ marginTop:10 }} onClick={() => setShowGooglePopup(false)}>
              {t.google_popup_later}
            </button>
          </div>
        </div>
      )}

      <nav className="bottom-nav">
        <div className="bottom-nav-inner">
          <button className={`bnav-btn ${activeNav==='menu'?'active':''}`}
            onClick={() => { setActiveNav('menu'); setMobileDetail(false); }}>
            <span className="bnav-icon"><MenuGridIcon /></span>
            <span className="bnav-label">{t.bnav_menu}</span>
          </button>
          {clientsEnabled && (
            <button className={`bnav-btn ${activeNav==='fav'?'active':''}`}
              onClick={() => { setActiveNav('fav'); setMobileDetail(false); }}>
              <span className="bnav-icon"><FavNavIcon active={activeNav==='fav'} /></span>
              <span className="bnav-label">{t.bnav_favorites}</span>
            </button>
          )}
          {shopEnabled && (
            <button className={`bnav-btn ${activeNav==='cart'?'active':''}`}
              onClick={() => { setActiveNav('cart'); setMobileDetail(false); }}>
              <span className="bnav-icon" style={{position:'relative'}}>
                <CartIcon size={21} />
                {(cartCount + extrasCount) > 0 && (
                  <span className="bnav-badge">{cartCount + extrasCount}</span>
                )}
              </span>
              <span className="bnav-label">{t.bnav_cart}</span>
            </button>
          )}
          {restaurant?.reservations_enabled && (
            <button className="bnav-btn" onClick={() => navigate(`/r/${slug}/reservations`)}>
              <span className="bnav-icon"><CalendarNavIcon /></span>
              <span className="bnav-label">{t.bnav_reservations}</span>
            </button>
          )}
          {(user || clientsEnabled) && (
            <button className="bnav-btn"
              onClick={() => user ? setShowProfile(true) : navigate(`/r/${slug}/login`)}>
              <span className="bnav-icon">
                {user
                  ? <div style={{width:28,height:28,minWidth:28,minHeight:28,borderRadius:'50%',background:'var(--green)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,flexShrink:0,aspectRatio:'1'}}>
                      {(profile?.full_name||user.email||'U').charAt(0).toUpperCase()}
                    </div>
                  : <UserNavIcon />
                }
              </span>
              <span className="bnav-label">{t.bnav_account}</span>
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}

// ── LANDING PAGE — exported from pages/LandingPage.tsx ────

// ── SUPER ADMIN LOGIN ─────────────────────────────────────
function SuperAdminLogin() {
  const navigate = useNavigate();
  const t = useT();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setError(''); setLoading(true);
    const { supabase } = await import('./lib/supabase');
    const { error: signInErr } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
    if (signInErr) { setError(signInErr.message); setLoading(false); return; }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setError(t.admin_login_failed); setLoading(false); return; }
    const { data: prof } = await supabase.from('profiles').select('role').eq('id', user.id).single();
    if (prof?.role !== 'superadmin') { await supabase.auth.signOut(); setError(t.admin_no_access); setLoading(false); return; }
    navigate('/super-admin');
    setLoading(false);
  };

  return (
    <div className="auth-bg admin-auth-bg">
      <div className="auth-card" style={{ position:'relative' }}>
        <LangToggle style={{ position:'absolute', top:16, right:16 }} />
        <div className="auth-logo admin-logo">⚙</div>
        <h1 className="auth-title">Super Admin</h1>
        <form className="auth-form" onSubmit={handle}>
          <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} required /></div>
          <div className="form-group"><label>{t.admin_login_password}</label><input type="password" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} required /></div>
          {error && <div className="auth-error">⚠ {error}</div>}
          <button type="submit" className="auth-btn admin-btn" disabled={loading}>{loading ? '...' : t.admin_login_btn}</button>
        </form>
      </div>
    </div>
  );
}

// ── ROOT APP ──────────────────────────────────────────────
export default function App() {
  return (
    <Suspense fallback={<div className="app-loader"><div className="loader-ring"/></div>}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/discover" element={<DiscoverPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/wizard" element={<WizardPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/super-admin/login" element={<SuperAdminLogin />} />
      <Route path="/super-admin" element={<SuperAdminPage />} />
      <Route path="/super-admin/restaurants/new" element={<SuperAdminRestaurantFormPage />} />
      <Route path="/super-admin/restaurants/:id/edit" element={<SuperAdminRestaurantFormPage />} />
      <Route path="/influencer/login" element={<InfluencerLoginPage />} />
      <Route path="/influencer" element={<InfluencerPortalPage />} />

      <Route path="/r/:slug" element={<RestaurantProvider><RestaurantLayout /></RestaurantProvider>}>
        <Route index element={<MenuPage />} />
        <Route path="login" element={<AuthPage />} />
        <Route path="lucky" element={<LuckyBitePage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="rewards" element={<RewardsPage />} />
        <Route path="reservations" element={<ReservationsPage />} />
        <Route path="admin/login" element={<AdminLoginPage />} />
        <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="courier/login" element={<CourierLoginPage />} />
        <Route path="courier" element={<CourierRoute><CourierDashboardPage /></CourierRoute>} />
        <Route path="waiter/login" element={<WaiterLoginPage />} />
        <Route path="waiter" element={<WaiterRoute><WaiterDashboardPage /></WaiterRoute>} />
        <Route path="kitchen/login" element={<KitchenLoginPage />} />
        <Route path="kitchen" element={<KitchenRoute><KitchenDashboardPage /></KitchenRoute>} />
        <Route path="*" element={<RestaurantCatchAll />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}
